import { BackButtonCWUI } from '@next-order/next-order-components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { getItemPrice } from '@next-order/next-order-engine';
import { toggleMobileCart, voucherFilterView } from 'containers/Home/actions';
import getImage from 'helpers/getImage';
import { buildBogoEstimateRows } from 'helpers/bogoVoucherHelper';
import { detectBogoDiscount } from 'helpers/order-engine/getBogoVoucherDiscount';
import plustwo from '../../assets/images/plus2.svg';

/**
 * BOGO voucher (type "4") Buy/Get item picker.
 *
 * Separate flow from the type-3 VoucherSpecialBox (which stays untouched):
 * shown when a BOGO voucher is applied but the cart cannot satisfy the deal.
 * Renders a "Buy" section (buyConfig.quantity slots) and a "Get" section
 * (getConfig.quantity slots). Clicking a slot filters the menu (same
 * voucherFilterView plumbing as type 3); once every slot is filled the
 * selected items are added to the order and the voucher is applied.
 */
export default function BogoVoucherView({
  voucherItem,
  selectedSpecials,
  addBogoVoucherItemsToOrder,
  hideOrder,
  storeConfig,
  sidebarAnimationClass,
}) {
  const dispatch = useDispatch();
  const activeBoxIndex = useSelector(
    (state) => state.homeReducer.voucherFilter.voucherBoxIndex
  );
  const halfHalfSetup = useSelector(
    (state) => state.layoutReducer.halfHalfSetup
  );

  const sections = voucherItem?.bogoSections || [];

  // Measure the voucher title so the scrollable slot area fills the space
  // between it and the fixed "Add to Order" bar (same approach as the
  // type-3 VoucherSpecialBox).
  const titleRef = useRef(null);
  const [titleHeight, setTitleHeight] = useState(0);
  useEffect(() => {
    setTitleHeight(titleRef.current?.clientHeight || 0);
  }, [titleRef]);

  // Global slot list: buy slots first, then get slots.
  const slots = useMemo(() => {
    const list = [];
    sections.forEach((section) => {
      for (let i = 0; i < section.quantity; i++) {
        list.push({
          boxIndex: list.length,
          section,
          isFirstOfSection: i === 0,
        });
      }
    });
    return list;
  }, [sections]);

  const getSelectionForSlot = (boxIndex) =>
    (selectedSpecials || []).find(
      (item) =>
        item.voucherId === voucherItem?._id && item.boxIndex === boxIndex
    );

  const applyFilterView = (slot) => {
    const { section } = slot;
    batch(() => {
      dispatch(toggleMobileCart(false));
      dispatch(
        voucherFilterView({
          isVoucherFilterApplied: true,
          voucherBoxIndex: slot.boxIndex,
          itemExist: getSelectionForSlot(slot.boxIndex) ? slot.boxIndex : -1,
          voucherMenuItems: section.menuItems,
          voucherMenuSizes: section.menuSizes,
          voucherSubModifiers: section.subModifiers,
          voucherMenuCategories: section.categories,
        })
      );
    });
  };

  // Desktop: auto-open the filter for the first empty slot (and auto-advance
  // after each selection) — same behavior the type-3 picker provides.
  const lastAutoAppliedRef = useRef(-1);
  const isSubmittedRef = useRef(false);
  useEffect(() => {
    // After "Add to Order" the picker stays mounted during the close
    // transition — never re-apply the menu filter at that point.
    if (isSubmittedRef.current) return;
    if (!slots.length) return;
    const isDesktop =
      window.innerWidth > 991 &&
      !(window.navigator.userAgent.match(/iPad/i) && window.innerHeight > 1250);
    if (!isDesktop) return;

    const firstEmpty = slots.find((slot) => !getSelectionForSlot(slot.boxIndex));
    if (firstEmpty && lastAutoAppliedRef.current !== firstEmpty.boxIndex) {
      lastAutoAppliedRef.current = firstEmpty.boxIndex;
      applyFilterView(firstEmpty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots, selectedSpecials]);

  const filledSelections = slots
    .map((slot) => getSelectionForSlot(slot.boxIndex))
    .filter(Boolean);
  const isSubmitEnabled = filledSelections.length === slots.length;

  // Live preview: total of the selected items minus the BOGO discount the
  // engine would grant for exactly these rows.
  const { totalCost, estimatedDiscount } = useMemo(() => {
    let total = 0;
    filledSelections.forEach((item) => {
      total += Number(getItemPrice(item, halfHalfSetup)) || 0;
    });
    const rows = buildBogoEstimateRows(filledSelections);
    const discount = detectBogoDiscount(voucherItem, rows).voucherDiscount;
    return { totalCost: total, estimatedDiscount: discount };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpecials, voucherItem, halfHalfSetup]);

  const payable = Math.max(totalCost - estimatedDiscount, 0).toFixed(2);

  // Benefit criteria shown next to the "Get" label, e.g. "Get Free item",
  // "Get 20% discount (max $5)", "Get $2 discount".
  const getBenefitText = (section) => {
    const benefit = voucherItem?.benefit || {};
    const benefitType = benefit.benefitType || '3';
    if (benefitType === '1') {
      const percentage = Number(benefit.discountValue) || 0;
      const cap = Number(benefit.maxDiscountAmount);
      return `${percentage}% discount${
        cap > 0 ? ` (max ${storeConfig.currency}${cap})` : ''
      }`;
    }
    if (benefitType === '2') {
      const value = Number(benefit.discountValue) || 0;
      return `${storeConfig.currency}${value} discount`;
    }
    return section.quantity > 1 ? 'Free items' : 'Free item';
  };

  const getSlotEmptyText = (slot) => {
    const { section } = slot;
    if (section.anyItem) {
      return section.maxItemPrice !== null
        ? `Any item up to ${storeConfig.currency}${section.maxItemPrice}`
        : 'Any item';
    }
    return section.description || '';
  };

  const truncate = (text, max = 10) =>
    text && text.length >= max ? text.substring(0, max).concat('...') : text;

  const renderSlot = (slot) => {
    const selection = getSelectionForSlot(slot.boxIndex);
    const imageUrl = selection
      ? getImage(selection.mediaPath || selection.urlS3)
      : null;
    return (
      <div
        key={`bogo-slot-${slot.boxIndex}`}
        className={activeBoxIndex === slot.boxIndex ? 'active bSp' : 'bSp'}
        onClick={() => applyFilterView(slot)}
      >
        <div className='innerSP'>
          {imageUrl ? (
            <img src={imageUrl} alt='' />
          ) : (
            <span>
              <img src={plustwo} alt='' />
              Add
            </span>
          )}
          <div className={selection ? 'activeName' : 'nonActive'}>
            {selection
              ? truncate(selection.name)
              : truncate(getSlotEmptyText(slot))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{ width: '100%', height: '100%' }}
      className={sidebarAnimationClass}
    >
      <div className='specialViewTopView'>
        <BackButtonCWUI
          extendedClassName='relative -top-1.5'
          onClick={hideOrder}
        />
        <div className='specialViewTop'>
          <span className='voucher'>Voucher</span>
        </div>
      </div>

      <div className='specialViewI'>
        <div
          ref={titleRef}
          className='detailsTitle sP'
          style={{ marginTop: 0, paddingBottom: 10 }}
        >
          {voucherItem?.description
            ? truncate(voucherItem.description, 100)
            : voucherItem?.voucherCode || voucherItem?.name || ''}
        </div>

        <div
          className='boxesSP'
          style={{ height: `calc(100% - ${titleHeight}px - 64px)` }}
        >
          {sections.map((section) => {
            const sectionSlots = slots.filter(
              (slot) => slot.section.key === section.key
            );
            return (
              // `clear: both` + the clearfix wrapper keep each section on its
              // own row — the slot boxes (.bSp) are floated at 50% width.
              <div
                key={`bogo-section-${section.key}`}
                style={{ clear: 'both' }}
              >
                <div
                  className='detailsTitle sP'
                  style={{
                    paddingTop: 6,
                    paddingBottom: 12,
                    paddingLeft: 14,
                    paddingRight: 14,
                  }}
                >
                  {section.label}
                  {section.quantity > 1 ? ` (${section.quantity})` : ''}
                  {section.key === 'get' && (
                    // plain sub-info text — the inline styles neutralize the
                    // .detailsTitle span pill-badge styling
                    <span
                      style={{
                        background: 'none',
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        borderRadius: 0,
                        padding: 0,
                        margin: '0 0 0 10px',
                        cursor: 'default',
                        whiteSpace: 'nowrap',
                        color: '#9b9b9b',
                        fontSize: '0.55em',
                        fontWeight: 400,
                      }}
                    >
                      {getBenefitText(section)}
                    </span>
                  )}
                </div>
                <div style={{ overflow: 'hidden' }}>
                  {sectionSlots.map(renderSlot)}
                </div>
              </div>
            );
          })}
        </div>

        <div className='spBtn'>
          <div
            onClick={() => {
              if (isSubmitEnabled) {
                isSubmittedRef.current = true;
                addBogoVoucherItemsToOrder();
              }
            }}
            className={
              isSubmitEnabled ? 'confirmBtn' : 'confirmBtn diableConfirm'
            }
          >
            Add to Order
            {isSubmitEnabled && (
              <span className='rb'>{` ${storeConfig.currency}${payable}`}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
